import React, { useState } from "react";
import {
  Box,
  Container,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Paper,
  FormGroup,
  Fade,
  Grow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import visaApi from "../apis/Visa.js";
import { ToastContainer, toast } from "react-toastify";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 16,
  background: "linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "scale(1.01)",
    },
    "&.Mui-focused": {
      transform: "scale(1.02)",
    },
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: "12px 32px",
  background: "linear-gradient(45deg, #d32f2f 30%, #f44336 90%)",
  boxShadow: "0 3px 5px 2px rgba(244, 67, 54, .3)",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    background: "linear-gradient(45deg, #f44336 30%, #d32f2f 90%)",
    transform: "scale(1.02)",
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#2196F3",
  },
  "& .MuiSvgIcon-root": {
    fontSize: 28,
  },
}));

const ReasonBox = styled(FormControlLabel)(({ theme }) => ({
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(0.75),
  borderRadius: 12,
  width: "100%",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: "rgba(33, 150, 243, 0.05)",
    transform: "translateX(8px)",
  },
}));

const UnsubscribeScreen = () => {
  const [email, setEmail] = useState("");
  const [reasons, setReasons] = useState({
    noLongerNeed: false,
    tooManyEmails: false,
    notRelevant: false,
    other: false,
  });

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleReasonChange = (event) => {
    setReasons({
      ...reasons,
      [event.target.name]: event.target.checked,
    });
  };

  const deviceInfo = {
    userAgent: window.navigator.userAgent,
    platform: window.navigator.platform,
    language: window.navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const response = await visaApi.unSubscribeFromEmailing({
      Email: email,
      Reason: reasons,
      User: "Web",
      DeviceInfo: deviceInfo,
    });
    if (response.success) {
      toast.success("Unsubscribed successfully");
      setEmail("");
      setReasons({
        noLongerNeed: false,
        tooManyEmails: false,
        notRelevant: false,
        other: false,
      });
      window.location.href = `/visas`;
    } else {
      toast.error("Failed to unsubscribe");
      console.error("Failed to unsubscribe", response.error);
    }
  };

  return (
    <Container maxWidth="sm">
      <ToastContainer position="top-center" newestOnTop={true} pauseOnHover theme="dark" />
      <Box sx={{ mt: 8, mb: 4 }}>
        <Fade in timeout={1000}>
          <StyledPaper>
            <Grow in timeout={800}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                sx={{
                  fontWeight: 600,
                  background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Unsubscribe
              </Typography>
            </Grow>
            <Fade in timeout={1200}>
              <Typography variant="body1" gutterBottom align="center" color="text.secondary" sx={{ mb: 4 }}>
                We're sorry to see you go. Please let us know why you're unsubscribing.
              </Typography>
            </Fade>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                mt: 4,
                "& > *": { mb: 3 },
              }}
            >
              <Fade in timeout={1400}>
                <div>
                  <StyledTextField
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    margin="normal"
                  />
                </div>
              </Fade>

              <Fade in timeout={1600}>
                <FormControl component="fieldset" sx={{ mt: 3, width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      fontWeight: 500,
                      color: "#1976d2",
                    }}
                  >
                    Reason for unsubscribing:
                  </Typography>
                  <FormGroup>
                    {Object.entries({
                      noLongerNeed: "I no longer need this service",
                      tooManyEmails: "I receive too many emails",
                      notRelevant: "Content is not relevant to me",
                      other: "Other",
                    }).map(([key, label], index) => (
                      <Grow in timeout={1000 + index * 200} key={key}>
                        <ReasonBox
                          control={<StyledCheckbox checked={reasons[key]} onChange={handleReasonChange} name={key} />}
                          label={label}
                        />
                      </Grow>
                    ))}
                  </FormGroup>
                </FormControl>
              </Fade>

              <Fade in timeout={2000}>
                <AnimatedButton type="submit" fullWidth variant="contained" size="large">
                  Unsubscribe
                </AnimatedButton>
              </Fade>
            </Box>
          </StyledPaper>
        </Fade>
      </Box>
    </Container>
  );
};

export default UnsubscribeScreen;
